import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import {
  ShoppingCartIcon,
  PlusIcon,
  MinusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  ChevronLeftIcon,
  CheckCircleIcon,
  SparklesIcon,
  FunnelIcon,
  ChatBubbleLeftRightIcon,
  ReceiptPercentIcon,
  Bars3Icon,
  Squares2X2Icon,
  GiftIcon,
  BeakerIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AgentStickyCart from '../../components/agent/AgentStickyCart';
import ImageLightbox from '../../components/ui/ImageLightbox';
import useAgentDraftCart from '../../hooks/useAgentDraftCart';

const AgentCreateOrderPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  
  // Draft cart hook for persistence
  const { 
    cart: draftCart, 
    isLoading: isDraftCartLoading, 
    isSaving: isDraftCartSaving,
    clearCart: clearDraftCart
  } = useAgentDraftCart(companyId);
  
  const [company, setCompany] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [draftCartLoaded, setDraftCartLoaded] = useState(false); // Track if we've loaded the draft
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState(''); // The actual search being applied
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [onlyInStock, setOnlyInStock] = useState(false); // Filter for products in stock
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false); // NEW: for search overlay
  
  // Lazy loading state for products
  const [productsPage, setProductsPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const [totalProductsCount, setTotalProductsCount] = useState(0);
  
  // Tabs state
  const [activeTab, setActiveTab] = useState('products'); // 'products' or 'bundles'
  const [bundles, setBundles] = useState([]);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [bundleCounter, setBundleCounter] = useState(1);
  
  // Cart state
  const [priceAdjustments, setPriceAdjustments] = useState({}); // { productId: { type, value, reason } }
  const [productNotes, setProductNotes] = useState({}); // { productId: note }
  const [orderNotes, setOrderNotes] = useState('');

  // Image Lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');
  
  // Save timer ref for debounced saving
  const saveTimerRef = useRef(null);

  // Intersection Observer for infinite scroll
  const observer = useRef();
  const lastProductElementRef = useCallback(node => {
    if (loadingMoreProducts) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreProducts) {
        setProductsPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMoreProducts, hasMoreProducts]);

  useEffect(() => {
    fetchCompany();
    fetchBundles();
    // Initial products fetch
    fetchProducts(1, true);
    // Reset draft loaded flag when company changes
    setDraftCartLoaded(false);
    setSelectedProducts([]);
  }, [companyId]);
  
  // Load draft cart items when both products and draft cart are ready
  useEffect(() => {
    if (!isDraftCartLoading && !loading && products.length > 0 && draftCart.items.length > 0 && !draftCartLoaded) {
      // Transform draft cart items to full product structure
      const restoredProducts = draftCart.items.map(item => {
        // Find the product in our fetched products
        const product = products.find(p => p.id === item.product_id);
        
        if (product) {
          // Merge draft cart item with full product data
          return {
            ...product,
            quantity: item.quantity || 1,
            unit_price: item.price || product.contract_price || product.final_price || product.price_b2b || 0,
            is_tester: item.is_tester || false,
            is_bonus: item.is_bonus || false,
            notes: item.notes || '',
            bundle_reference: item.bundle_reference || null,
            is_bundle_item: item.is_bundle_item || false
          };
        } else {
          // Product not found in current fetch - keep minimal data
          return {
            id: item.product_id,
            name: item.product_name || 'מוצר לא זמין',
            sku: item.sku || '',
            images: item.product_image ? [item.product_image] : [],
            quantity: item.quantity || 1,
            unit_price: item.price || 0,
            is_tester: item.is_tester || false,
            is_bonus: item.is_bonus || false,
            notes: item.notes || '',
            bundle_reference: item.bundle_reference || null,
            is_bundle_item: item.is_bundle_item || false,
            _notFound: true
          };
        }
      });
      
      setSelectedProducts(restoredProducts);
      setDraftCartLoaded(true);
      
      if (restoredProducts.length > 0) {
        toast.success(`נטענה טיוטת הזמנה עם ${restoredProducts.length} פריטים`, { 
          duration: 3000,
          icon: '📋'
        });
      }
    } else if (!isDraftCartLoading && !loading && draftCart.items.length === 0 && !draftCartLoaded) {
      // No draft cart - mark as loaded
      setDraftCartLoaded(true);
    }
  }, [isDraftCartLoading, loading, products, draftCart.items, draftCartLoaded]);
  
  // Save selectedProducts to draft cart when they change (debounced)
  useEffect(() => {
    if (!draftCartLoaded || selectedProducts === null) return;
    
    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    
    // Debounce save - 2 seconds after last change
    saveTimerRef.current = setTimeout(async () => {
      try {
        // Transform selectedProducts to draft cart format
        const cartData = {
          items: selectedProducts.map(p => ({
            product_id: p.id,
            product_name: p.name,
            sku: p.sku || '',
            quantity: p.quantity,
            price: p.unit_price || p.contract_price || p.final_price || 0,
            product_image: p.images?.[0] || '',
            notes: p.notes || '',
            is_tester: p.is_tester || false,
            is_bonus: p.is_bonus || false,
            bundle_reference: p.bundle_reference || null,
            is_bundle_item: p.is_bundle_item || false
          }))
        };
        
        // Save via API directly (hook's auto-save might conflict)
        await axios.post(`/api/field-agent/draft-carts/${companyId}`, {
          cart_data: cartData
        });
      } catch (error) {
        console.error('Error saving draft cart:', error);
      }
    }, 2000);
    
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [selectedProducts, companyId, draftCartLoaded]);

  // Reset products when activeSearch or category filter changes
  useEffect(() => {
    // Use 'searching' state for better UX - show overlay instead of clearing
    setSearching(true);
    setProductsPage(1);
    setHasMoreProducts(true);
    
    // Fetch new products - they will replace old ones in fetchProducts
    fetchProducts(1, true);
  }, [activeSearch, categoryFilter]);
  
  // Handle search when user presses Enter or clicks search button
  const handleSearch = () => {
    setActiveSearch(searchTerm.trim());
  };

  // Load more products when page changes
  useEffect(() => {
    if (productsPage > 1) {
      fetchProducts(productsPage, false);
    }
  }, [productsPage]);

  const fetchCompany = async () => {
    try {
      const response = await axios.get(`/api/field-agent/companies`);
      const companies = response.data.companies || [];
      const foundCompany = companies.find(c => c.company.id === companyId);
      if (foundCompany) {
        setCompany(foundCompany.company);
      }
    } catch (error) {
      console.error('Error fetching company:', error);
    }
  };

  const fetchProducts = async (page = 1, isInitial = false) => {
    if (loadingMoreProducts && !isInitial) return;
    
    try {
      if (isInitial) {
      setLoading(true);
      } else {
        setLoadingMoreProducts(true);
      }

      // Build query params
      const params = {
        page: page,
        limit: 50,
        status: 'active'
      };
      if (activeSearch) {
        params.search = activeSearch;
      }
      if (categoryFilter) {
        params.category_id = categoryFilter; // categoryFilter is now a category ID
      }

      const [productsRes, contractsRes] = await Promise.all([
        axios.get(`/api/products`, { params }),
        page === 1 ? axios.get(`/api/contract-pricing/company/${companyId}`) : Promise.resolve({ data: { contracts: [] } })
      ]);

      let fetchedProducts = productsRes.data.products || [];
      const contracts = contractsRes.data.contracts || [];

      // Store total count
      if (productsRes.data.total) {
        setTotalProductsCount(productsRes.data.total);
      }

      // Extract unique categories (only on first page)
      if (page === 1 && fetchedProducts.length > 0) {
        const categoriesMap = new Map();
        fetchedProducts.forEach(p => {
          if (p.category && p.category.id && p.category.name) {
            categoriesMap.set(p.category.id, p.category);
          }
        });
        setCategories(Array.from(categoriesMap.values()));
      }

      // Apply contract pricing
      fetchedProducts = fetchedProducts.map(product => {
        const applicableContracts = contracts.filter(contract => {
          if (contract.product_id && contract.product_id === product.id) return true;
          if (contract.category_id && contract.category_id === product.category_id) return true;
          return false;
        });

        let finalPrice = parseFloat(product.price_b2b || product.price_b2c || 0);
        const originalPrice = parseFloat(product.price_b2c || 0);
        let appliedRules = [];

        applicableContracts.forEach(contract => {
          if (contract.pricing_type === 'fixed_price' && contract.fixed_price) {
            finalPrice = parseFloat(contract.fixed_price);
            appliedRules.push(`מחיר קבוע: ₪${finalPrice}`);
          } else if (contract.pricing_type === 'percentage_discount' && contract.discount_percentage) {
            const discount = finalPrice * (parseFloat(contract.discount_percentage) / 100);
            finalPrice -= discount;
            appliedRules.push(`הנחה ${contract.discount_percentage}%`);
          }
        });

        return {
          ...product,
          contract_price: finalPrice,
          final_price: finalPrice,
          original_b2c_price: originalPrice,
          savings: originalPrice - finalPrice,
          savings_percentage: originalPrice > 0 ? (((originalPrice - finalPrice) / originalPrice) * 100).toFixed(0) : 0,
          applied_rules: appliedRules
        };
      });

      // Append or replace products
      setProducts(prev => page === 1 ? fetchedProducts : [...prev, ...fetchedProducts]);
      setHasMoreProducts(fetchedProducts.length === 50); // If we got full page, there might be more
      
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('שגיאה בטעינת המוצרים');
      setHasMoreProducts(false);
    } finally {
      if (isInitial) {
      setLoading(false);
        setSearching(false); // Clear searching state
      } else {
        setLoadingMoreProducts(false);
      }
    }
  };

  const fetchBundles = async () => {
    try {
      setLoadingBundles(true);
      const response = await axios.get('/api/bundles?customer_type=b2b&limit=50');
      setBundles(response.data.bundles || []);
    } catch (error) {
      console.error('Error fetching bundles:', error);
      toast.error('שגיאה בטעינת החבילות');
    } finally {
      setLoadingBundles(false);
    }
  };

  // Sort products (filtering is now done on backend via pagination)
  const filteredProducts = useMemo(() => {
    // Products are already filtered by backend based on search and category
    // Filter by stock if onlyInStock is true
    let filtered = [...products];
    
    if (onlyInStock) {
      filtered = filtered.filter(p => p.stock_b2b > 0);
    }

    // Sort the filtered products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name, 'he');
        case 'price_low':
          return (a.final_price || 0) - (b.final_price || 0);
        case 'price_high':
          return (b.final_price || 0) - (a.final_price || 0);
        case 'stock':
          return (b.stock_b2b || 0) - (a.stock_b2b || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [products, sortBy, onlyInStock]);

  const addProduct = (product, options = {}) => {
    // options: { is_tester: boolean, is_bonus: boolean }
    const existing = selectedProducts.find(p => 
      p.id === product.id && 
      p.is_tester === (options.is_tester || false) && 
      p.is_bonus === (options.is_bonus || false)
    );
    
    if (existing) {
      // Update existing item quantity (for both tester and bonus items)
      setSelectedProducts(selectedProducts.map(p =>
        p.id === product.id && 
        p.is_tester === (options.is_tester || false) && 
        p.is_bonus === (options.is_bonus || false)
          ? { ...p, quantity: p.quantity + 1 }
          : p
      ));
      const itemType = options.is_bonus ? 'בונוס' : options.is_tester ? 'טסטר' : '';
      toast.success(`הכמות עודכנה${itemType ? ` (${itemType})` : ''}`);
    } else {
      let price = parseFloat(product.contract_price || product.final_price || product.price_b2b || product.price_b2c || 0);
      
      // If tester or bonus, price is always 0
      if (options.is_tester || options.is_bonus) {
        price = 0;
      }
      
      // Build notes
      let notes = '';
      if (options.is_tester) {
        notes = 'טסטר - לספק בעלות 0';
      }
      if (options.is_bonus) {
        notes = notes ? `${notes}\nבונוס - מתנה` : 'בונוס - מתנה';
      }
      
      const newProduct = {
        ...product,
        quantity: 1,
        unit_price: price,
        is_tester: options.is_tester || false,
        is_bonus: options.is_bonus || false,
        notes: notes
      };
      
      setSelectedProducts([...selectedProducts, newProduct]);
      const itemType = options.is_bonus ? 'בונוס' : options.is_tester ? 'טסטר' : '';
      toast.success(`המוצר ${itemType ? `(${itemType}) ` : ''}נוסף לעגלה`);
    }
  };

  const handleAddTester = (product) => {
    // Always add exactly 1 tester item per click, regardless of any other quantity
    addProduct(product, { is_tester: true });
  };

  const handleAddBonus = (product) => {
    // Always add exactly 1 bonus item per click, regardless of any other quantity
    addProduct(product, { is_bonus: true });
  };

  const updateQuantity = (productId, newQuantity, isTester = false, isBonus = false) => {
    if (newQuantity < 1) {
      removeProduct(productId, isTester, isBonus);
      return;
    }
    setSelectedProducts(selectedProducts.map(p =>
      p.id === productId && 
      p.is_tester === isTester && 
      p.is_bonus === isBonus
        ? { ...p, quantity: newQuantity }
        : p
    ));
  };

  const removeProduct = (productId, isTester = false, isBonus = false) => {
    setSelectedProducts(selectedProducts.filter(p =>
      !(p.id === productId && 
        p.is_tester === isTester && 
        p.is_bonus === isBonus)
    ));
    // Note: priceAdjustments and productNotes use productId as key, 
    // so we keep them as is (they're per product, not per item type)
    toast.success('המוצר הוסר');
  };

  const applyDiscount = (productId, type, value, reason) => {
    const product = selectedProducts.find(p => p.id === productId);
    
    // מניעת הנחה נוספת על פריטים מחבילה
    if (product?.is_bundle_item) {
      toast.error('לא ניתן להוסיף הנחה נוספת על פריטים מחבילה');
      return;
    }
    
    setPriceAdjustments(prev => ({
      ...prev,
      [productId]: { type, value, reason }
    }));
  };

  const removeDiscount = (productId) => {
    setPriceAdjustments(prev => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  const calculateItemPrice = (product) => {
    // Tester and bonus items are always 0 price
    if (product.is_tester || product.is_bonus) {
      return 0;
    }
    
    const basePrice = parseFloat(product.unit_price || 0);
    const adjustment = priceAdjustments[product.id];
    
    if (!adjustment) return basePrice;

    if (adjustment.type === 'fixed') {
      return parseFloat(adjustment.value);
    } else if (adjustment.type === 'percentage') {
      return basePrice * (1 - parseFloat(adjustment.value) / 100);
    }
    
    return basePrice;
  };

  const calculateTotal = () => {
    const subtotal = selectedProducts.reduce((sum, product) => {
      const price = calculateItemPrice(product);
      return sum + (price * product.quantity);
    }, 0);
    
    const tax = subtotal * 0.18;
    const total = subtotal + tax;
    
    return { subtotal, tax, total };
  };

  // Bundle functions
  const addBundle = async (bundle) => {
    try {
      // יצירת מזהה ייחודי לחבילה
      const bundleRef = `BUNDLE-${String(bundleCounter).padStart(3, '0')}`;
      setBundleCounter(prev => prev + 1);
      
      // טעינת פרטי מוצרים מהחבילה
      const response = await axios.get(`/api/bundles/${bundle.id}/products`);
      const bundleProducts = response.data.products;
      
      // הוספת כל מוצר מהחבילה כפריט נפרד
      const newItems = bundleProducts.map(product => ({
        ...product,
        id: product.id,
        quantity: product.quantity || 1,
        unit_price: product.bundle_price, // המחיר המוזל מהחבילה
        bundle_reference: bundleRef,
        is_bundle_item: true,
        bundle_item_price: product.bundle_price,
        bundle_title: bundle.title,
        notes: `חלק מחבילה #${bundleRef} - ${bundle.title}`
      }));
      
      setSelectedProducts([...selectedProducts, ...newItems]);
      toast.success(`חבילה "${bundle.title}" נוספה בהצלחה!`);
      
      // Switch to products tab to see the cart
      setActiveTab('products');
    } catch (error) {
      console.error('Error adding bundle:', error);
      toast.error('שגיאה בהוספת החבילה');
    }
  };

  const addSingleItemFromBundle = (bundleItem) => {
    // מוצר נוסף ללא bundle_reference = מחיר מלא + הנחת חברה
    const product = products.find(p => p.id === bundleItem.id);
    if (!product) {
      toast.error('המוצר לא נמצא');
      return;
    }
    
    const fullPrice = parseFloat(product.final_price); // כבר כולל הנחת חברה
    
    const newItem = {
      ...product,
      quantity: 1,
      unit_price: fullPrice,
      bundle_reference: null,
      is_bundle_item: false,
      notes: `פריט נוסף (מחיר מלא + הנחת חברה)`
    };
    
    setSelectedProducts([...selectedProducts, newItem]);
    toast.success(`${product.name} נוסף במחיר מלא (₪${fullPrice.toFixed(2)})`);
  };

  const removeBundle = (bundleRef) => {
    setSelectedProducts(selectedProducts.filter(p => p.bundle_reference !== bundleRef));
    toast.success('החבילה הוסרה');
  };

  // Group items by bundle
  const groupedItems = useMemo(() => {
    return selectedProducts.reduce((acc, item) => {
      if (item.bundle_reference) {
        if (!acc.bundles[item.bundle_reference]) {
          acc.bundles[item.bundle_reference] = {
            items: [],
            title: item.bundle_title || item.bundle_reference
          };
        }
        acc.bundles[item.bundle_reference].items.push(item);
      } else {
        acc.regular.push(item);
      }
      return acc;
    }, { bundles: {}, regular: [] });
  }, [selectedProducts]);

  const handleSubmitOrder = async () => {
    if (selectedProducts.length === 0) {
      toast.error('נא להוסיף מוצרים להזמנה');
      return;
    }

    try {
      setSubmitting(true);

      // Prepare price adjustments documentation
      const priceAdjustmentsData = selectedProducts
        .filter(p => priceAdjustments[p.id])
        .map(p => {
          const adjustment = priceAdjustments[p.id];
          const originalPrice = parseFloat(p.unit_price);
          const adjustedPrice = calculateItemPrice(p);
          
          return {
            product_id: p.id,
            product_name: p.name,
            original_price: originalPrice,
            adjusted_price: adjustedPrice,
            discount: originalPrice - adjustedPrice,
            discount_percent: ((originalPrice - adjustedPrice) / originalPrice * 100).toFixed(1),
            reason: adjustment.reason || 'התאמת מחיר',
            adjustment_type: adjustment.type,
            adjustment_value: adjustment.value
          };
        });

      const orderData = {
        company_id: companyId,
        items: selectedProducts.map(p => ({
          product_id: p.id,
          quantity: p.quantity,
          unit_price: calculateItemPrice(p),
          bundle_reference: p.bundle_reference || null,
          is_bundle_item: p.is_bundle_item || false,
          bundle_item_price: p.bundle_item_price || null,
          notes: p.notes || productNotes[p.id] || undefined,
          is_tester: p.is_tester || false,
          is_bonus: p.is_bonus || false
        })),
        payment_method: 'company_credit',
        notes: orderNotes || 'הזמנה שנוצרה על ידי סוכן שטח',
        price_adjustments: priceAdjustmentsData.length > 0 ? priceAdjustmentsData : undefined
      };

      await axios.post('/api/field-agent/orders', orderData);
      
      // Clear the draft cart after successful order
      try {
        await clearDraftCart();
      } catch (e) {
        console.error('Error clearing draft cart:', e);
      }

      if (priceAdjustmentsData.length > 0) {
        toast.success(`ההזמנה נוצרה בהצלחה עם ${priceAdjustmentsData.length} התאמות מחיר!`);
      } else {
        toast.success('ההזמנה נוצרה בהצלחה!');
      }
      navigate('/agent/orders');
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.message || 'שגיאה ביצירת ההזמנה');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const { subtotal, tax, total } = calculateTotal();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">טוען מוצרים...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <button
                type="button"
                onClick={() => navigate('/agent/companies')}
                aria-label="חזור לחברות"
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <ChevronLeftIcon className="h-5 w-5 text-gray-600" aria-hidden="true" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <SparklesIcon className="h-5 w-5 text-primary-600 flex-shrink-0" aria-hidden="true" />
                  <h1 className="text-base sm:text-xl font-bold bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent truncate">
                    צור הזמנה חדשה
                  </h1>
                </div>
                {company && (
                  <p className="text-xs text-gray-600 mt-0.5 truncate">
                    <span className="font-medium">{company.name}</span>
                  </p>
                )}
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="flex items-center gap-2 w-full sm:w-auto justify-end sm:justify-start">
              {/* Draft Save Indicator */}
              {selectedProducts.length > 0 && (
                <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] sm:text-xs transition-all ${
                  isDraftCartSaving 
                    ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' 
                    : 'bg-green-50 text-green-700 border border-green-200'
                }`}>
                  {isDraftCartSaving ? (
                    <>
                      <div className="animate-spin h-3 w-3 border border-yellow-400 border-t-transparent rounded-full"></div>
                      <span className="hidden sm:inline">שומר...</span>
                    </>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="h-3 w-3" aria-hidden="true" />
                      <span className="hidden sm:inline">טיוטה נשמרה</span>
                    </>
                  )}
                </div>
              )}
              <div className="text-center px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                <div className="text-[9px] sm:text-[10px] text-blue-700">מוצרים</div>
                <div className="text-xs sm:text-sm font-bold text-blue-900">{selectedProducts.length}</div>
              </div>
              <div className="text-center px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                <div className="text-[9px] sm:text-[10px] text-green-700">סה"כ</div>
                <div className="text-xs sm:text-sm font-bold text-green-900" dir="ltr">₪{total.toFixed(0)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-3 sm:py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Products Section */}
          <div className="lg:col-span-2 space-y-3 sm:space-y-4">
            {/* Filters Bar */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-3">
              <div className="space-y-3">
                {/* Search */}
                <div className="relative flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1 flex gap-2">
                    <div className="relative flex-1">
                  <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden="true" />
                  <input
                    type="text"
                        autoComplete="off"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearch();
                          }
                        }}
                    placeholder="חפש מוצר..."
                    aria-label="חיפוש מוצרים"
                    className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                    </div>
                    <button
                      type="button"
                      onClick={handleSearch}
                      className="px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium min-w-[44px]"
                      title="חפש"
                    >
                      <MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />
                      <span className="hidden sm:inline">חפש</span>
                    </button>
                    {activeSearch && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchTerm('');
                          setActiveSearch('');
                        }}
                        className="px-2 sm:px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm min-w-[44px] flex items-center justify-center"
                        title="נקה חיפוש"
                      >
                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
                {(activeSearch || onlyInStock) && (
                  <div className="text-xs text-gray-500 bg-blue-50 px-3 py-2 rounded-lg flex items-center gap-2 flex-wrap">
                    {activeSearch && (
                      <span>
                        <span className="font-medium">מחפש:</span> "{activeSearch}"
                    </span>
                  )}
                    {activeSearch && onlyInStock && <span>•</span>}
                    {onlyInStock && (
                      <span className="text-green-600 font-medium">רק מוצרים במלאי</span>
                    )}
                    <span>•</span>
                    <span className="font-medium">{filteredProducts.length} תוצאות</span>
                    {totalProductsCount > products.length && <span> מתוך {totalProductsCount}</span>}
                </div>
                )}

                {/* Tabs */}
                <div className="flex gap-1.5 sm:gap-2 border-b border-gray-200 pb-2">
                  <button
                    type="button"
                    onClick={() => setActiveTab('products')}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-t-lg font-medium transition-all text-sm ${
                      activeTab === 'products'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Squares2X2Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                    <span>מוצרים</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('bundles')}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-t-lg font-medium transition-all text-sm ${
                      activeTab === 'bundles'
                        ? 'bg-purple-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <GiftIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                    <span>חבילות</span>
                    {bundles.length > 0 && (
                      <span className="bg-white/30 px-1.5 py-0.5 rounded-full text-xs">
                        {bundles.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* Filters Row - Only for products tab */}
                {activeTab === 'products' && (
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2">
                  {/* Category Filter */}
                  <div className="flex items-center gap-2 flex-1 min-w-full sm:min-w-[180px]">
                    <FunnelIcon className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      aria-label="סינון לפי קטגוריה"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    >
                      <option value="">כל הקטגוריות</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Sort */}
                  <div className="flex items-center gap-2 flex-1 min-w-full sm:min-w-[140px]">
                    <Bars3Icon className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      aria-label="מיון"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    >
                      <option value="name">שם</option>
                      <option value="price_low">מחיר נמוך-גבוה</option>
                      <option value="price_high">מחיר גבוה-נמוך</option>
                      <option value="stock">מלאי</option>
                    </select>
                  </div>

                  {/* View Mode */}
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => setViewMode('grid')}
                      aria-label="תצוגת רשת"
                      className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500'}`}
                    >
                      <Squares2X2Icon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('list')}
                      aria-label="תצוגת רשימה"
                      className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500'}`}
                    >
                      <Bars3Icon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                  
                  {/* Stock Filter Button */}
                  <button
                    type="button"
                    onClick={() => setOnlyInStock(!onlyInStock)}
                    className={`flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all min-h-[40px] ${
                      onlyInStock
                        ? 'bg-blue-500 text-white shadow-md hover:bg-blue-600 active:bg-blue-700'
                        : 'bg-green-500 text-white shadow-md hover:bg-green-600 active:bg-green-700'
                    }`}
                  >
                    <CheckCircleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                    <span className="hidden sm:inline whitespace-nowrap">{onlyInStock ? 'הצג כל המוצרים' : 'הצג רק במלאי'}</span>
                    <span className="sm:hidden">{onlyInStock ? 'הכל' : 'מלאי'}</span>
                  </button>
                </div>
                )}
              </div>
            </div>

            {/* Products Tab Content */}
            {activeTab === 'products' && (
            <>
            {/* Products Grid/List */}
            <div className="relative">
              {/* Searching Overlay */}
              {searching && (
                <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                  <div className="bg-white shadow-lg rounded-lg p-4 flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent"></div>
                    <span className="text-gray-700 font-medium">מחפש...</span>
                  </div>
                </div>
              )}
              
              {filteredProducts.length === 0 && !searching ? (
              <div className="bg-white rounded-lg shadow-sm border-2 border-dashed border-gray-300 p-12 text-center">
                <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-300 mb-3" aria-hidden="true" />
                <p className="text-gray-500 font-medium">לא נמצאו מוצרים</p>
                <p className="text-sm text-gray-400 mt-1">נסה שינוי של הפילטרים</p>
              </div>
            ) : (
                <>
              <div className={viewMode === 'grid' 
                  ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4' 
                  : 'space-y-2 sm:space-y-3'
              }>
                {filteredProducts.map((product, index) => {
                  const productImage = product.images?.[0] || product.image_url;
                  const isInCart = selectedProducts.find(p => p.id === product.id);
                  const isLastElement = index === filteredProducts.length - 1;
                  
                  if (viewMode === 'list') {
                    // List View - Compact
                    return (
                      <div
                        key={product.id}
                        ref={isLastElement ? lastProductElementRef : null}
                        className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-all p-3 border ${
                          isInCart ? 'border-primary-500 ring-1 ring-primary-200' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Image */}
                          <div className="w-20 h-20 flex-shrink-0 bg-gray-50 rounded overflow-hidden">
                            {productImage ? (
                              <img
                                src={productImage}
                                alt={`תמונת ${product.name}`}
                                loading="lazy"
                                className="w-full h-full object-contain p-1 cursor-pointer hover:opacity-75 transition-opacity"
                                onClick={() => {
                                  setLightboxImage(productImage);
                                  setLightboxAlt(product.name);
                                }}
                                title="לחץ להגדלה"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <PhotoIcon className="h-8 w-8 text-gray-300" aria-hidden="true" />
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-semibold text-gray-900 truncate">{product.name}</h3>
                            {product.sku && (
                              <p className="text-xs text-gray-500">מק"ט: {product.sku}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              {product.savings > 0 && (
                                <span className="text-xs font-bold text-green-600">
                                  חיסכון {product.savings_percentage}%
                                </span>
                              )}
                              {product.stock_b2b !== null && (
                                <span className={`text-xs ${
                                  product.stock_b2b > 50 ? 'text-green-600' :
                                  product.stock_b2b > 0 ? 'text-yellow-600' : 'text-red-600'
                                }`}>
                                  מלאי: {product.stock_b2b}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Price & Action */}
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-3">
                              <div className="text-left">
                                <div className="text-lg font-bold text-primary-600">
                                  ₪{parseFloat(product.final_price).toFixed(2)}
                                </div>
                                {product.savings > 0 && (
                                  <div className="text-xs text-gray-400 line-through">
                                    ₪{parseFloat(product.original_b2c_price).toFixed(2)}
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => addProduct(product)}
                                disabled={product.stock_b2b <= 0}
                                aria-label={`הוסף ${product.name}`}
                                className={`p-3 rounded-lg transition-all touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center ${
                                  product.stock_b2b <= 0
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : isInCart
                                      ? 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700'
                                      : 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 shadow-md'
                                }`}
                              >
                                {isInCart ? (
                                  <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
                                ) : (
                                  <PlusIcon className="h-5 w-5" aria-hidden="true" />
                                )}
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleAddTester(product)}
                                disabled={product.stock_b2b <= 0}
                                className="flex-1 px-2 py-1.5 border border-blue-300 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium flex items-center justify-center gap-1 touch-manipulation"
                                aria-label="הוסף טסטר"
                              >
                                <BeakerIcon className="h-3.5 w-3.5" />
                                <span>טסטר</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAddBonus(product)}
                                disabled={product.stock_b2b <= 0}
                                className="flex-1 px-2 py-1.5 border border-purple-300 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium flex items-center justify-center gap-1 touch-manipulation"
                                aria-label="הוסף בונוס"
                              >
                                <GiftIcon className="h-3.5 w-3.5" />
                                <span>בונוס</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Grid View - Original
                  return (
                    <div
                      key={product.id}
                      ref={isLastElement ? lastProductElementRef : null}
                      className={`bg-white rounded-lg shadow-sm hover:shadow-lg transition-all overflow-hidden border ${
                        isInCart ? 'border-primary-500 ring-2 ring-primary-100' : 'border-gray-200'
                      }`}
                    >
                      {/* Image */}
                      <div className="relative h-48 sm:h-56 md:h-64 lg:h-56 bg-gray-50">
                        {productImage ? (
                          <img
                            src={productImage}
                            alt={`תמונת ${product.name}`}
                            loading="lazy"
                            className="w-full h-full object-contain p-2 sm:p-3 cursor-pointer hover:opacity-75 transition-opacity"
                            onClick={() => {
                              setLightboxImage(productImage);
                              setLightboxAlt(product.name);
                            }}
                            title="לחץ להגדלה"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <PhotoIcon className="h-12 w-12 text-gray-300" aria-hidden="true" />
                          </div>
                        )}
                        {isInCart && (
                          <div className="absolute top-2 right-2 bg-primary-600 text-white rounded-full px-2 py-0.5 text-xs font-bold shadow-lg animate-pulse">
                            <CheckCircleIcon className="h-3 w-3 inline ml-0.5" aria-hidden="true" />
                            בעגלה
                          </div>
                        )}
                        {product.stock_b2b !== null && (
                          <div className="absolute bottom-2 left-2 z-10">
                            <span className={`px-2 py-1 rounded-full text-xs font-bold shadow-lg ${
                              product.stock_b2b > 50 
                                ? 'bg-green-500 text-white' 
                                : product.stock_b2b > 0 
                                  ? 'bg-yellow-500 text-white'
                                  : 'bg-red-500 text-white'
                            }`}>
                              {product.stock_b2b}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-3">
                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2 min-h-[2.5rem]">
                          {product.name}
                        </h3>
                        {product.sku && (
                          <p className="text-xs text-gray-500 mb-2">מק"ט: {product.sku}</p>
                        )}
                        
                        <div className="space-y-2">
                          <div className="flex items-baseline justify-between gap-2 mb-2">
                            <div>
                              <div className="text-lg font-bold text-primary-600">
                                ₪{parseFloat(product.final_price).toFixed(2)}
                              </div>
                              {product.savings > 0 && (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-gray-400 line-through">
                                    ₪{parseFloat(product.original_b2c_price).toFixed(2)}
                                  </span>
                                  <span className="text-xs font-bold text-green-600">
                                    -{product.savings_percentage}%
                                  </span>
                                </div>
                              )}
                            </div>
                            </div>
                            <button
                            type="button"
                              onClick={() => addProduct(product)}
                            disabled={product.stock_b2b <= 0}
                              aria-label={`הוסף ${product.name}`}
                            className={`w-full px-4 py-2.5 rounded-lg font-bold transition-all text-sm touch-manipulation min-h-[48px] flex items-center justify-center gap-2 ${
                              product.stock_b2b <= 0
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : isInCart
                                  ? 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700 shadow-md'
                                  : 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 shadow-md hover:shadow-lg'
                              }`}
                            >
                            {isInCart ? (
                              <>
                                <CheckCircleIcon className="h-5 w-5" />
                                <span>נוסף לעגלה</span>
                              </>
                            ) : (
                              <>
                                <PlusIcon className="h-5 w-5" />
                                <span>הוסף לעגלה</span>
                              </>
                            )}
                            </button>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => handleAddTester(product)}
                              disabled={product.stock_b2b <= 0}
                              className="px-2 py-2 border border-blue-300 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium flex items-center justify-center gap-1 touch-manipulation min-h-[44px]"
                              aria-label="הוסף טסטר"
                            >
                              <BeakerIcon className="h-4 w-4 flex-shrink-0" />
                              <span>טסטר</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAddBonus(product)}
                              disabled={product.stock_b2b <= 0}
                              className="px-2 py-2 border border-purple-300 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium flex items-center justify-center gap-1 touch-manipulation min-h-[44px]"
                              aria-label="הוסף בונוס"
                            >
                              <GiftIcon className="h-4 w-4 flex-shrink-0" />
                              <span>בונוס</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Loading more indicator */}
              {loadingMoreProducts && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary-200 border-t-primary-600 mx-auto mb-2"></div>
                    <p className="text-gray-600 text-sm">טוען מוצרים נוספים...</p>
                  </div>
                </div>
              )}

              {/* End of products indicator */}
              {!hasMoreProducts && filteredProducts.length > 0 && (
                <div className="text-center py-6">
                  <p className="text-gray-500 text-sm">
                    הוצגו כל המוצרים ({filteredProducts.length} מוצרים)
                  </p>
              </div>
            )}
              </>
            )}
            </div>
            </>
            )}

            {/* Bundles Tab Content */}
            {activeTab === 'bundles' && (
              <div className="space-y-4">
                {loadingBundles ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600 mx-auto mb-3"></div>
                      <p className="text-gray-600 font-medium">טוען חבילות...</p>
                    </div>
                  </div>
                ) : bundles.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-sm border-2 border-dashed border-gray-300 p-12 text-center">
                    <GiftIcon className="mx-auto h-12 w-12 text-gray-300 mb-3" aria-hidden="true" />
                    <p className="text-gray-500 font-medium">אין חבילות B2B זמינות</p>
                    <p className="text-sm text-gray-400 mt-1">חבילות יופיעו כאן כשיהיו זמינות</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {bundles.map(bundle => {
                      const bundleImage = bundle.bundle_image || bundle.images?.[0];
                      const products = bundle.items?.map(item => item.product) || [];
                      
                      return (
                        <div
                          key={bundle.id}
                          className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all overflow-hidden border-2 border-gray-200 hover:border-purple-500"
                        >
                          {/* Bundle Image */}
                          <div className="relative h-40 sm:h-48 bg-gradient-to-br from-purple-100 to-purple-200">
                            {bundleImage ? (
                              <img
                                src={bundleImage}
                                alt={bundle.title}
                                className="w-full h-full object-contain p-2 sm:p-3 cursor-pointer hover:opacity-75 transition-opacity"
                                onClick={() => {
                                  setLightboxImage(bundleImage);
                                  setLightboxAlt(bundle.title);
                                }}
                                title="לחץ להגדלה"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <GiftIcon className="h-16 w-16 text-purple-400" aria-hidden="true" />
                              </div>
                            )}
                            {/* Status Badge */}
                            {bundle.status && bundle.status !== 'active' && (
                              <div className="absolute top-2 right-2">
                                <span className={`px-2 py-1 text-white text-xs font-bold rounded-full shadow-lg ${
                                  bundle.status === 'not_started' ? 'bg-blue-500' : 
                                  bundle.status === 'expired' ? 'bg-red-500' : 'bg-gray-500'
                                }`}>
                                  {bundle.status_message || bundle.status}
                                </span>
                              </div>
                            )}
                            
                            {/* Discount Badge */}
                            <div className={`absolute ${bundle.status && bundle.status !== 'active' ? 'top-8' : 'top-2'} right-2 bg-green-500 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-bold shadow-lg`}>
                              חיסכון {parseFloat(bundle.discount_percentage).toFixed(0)}%
                            </div>
                          </div>

                          {/* Bundle Content */}
                          <div className="p-3 sm:p-4">
                            <h3 className="font-bold text-base sm:text-lg text-gray-900 mb-2 line-clamp-2">
                              {bundle.title}
                            </h3>

                            {/* Prices */}
                            <div className="flex items-center gap-2 mb-3">
                              <span className="text-gray-400 line-through text-xs sm:text-sm">
                                ₪{parseFloat(bundle.regular_price).toFixed(2)}
                              </span>
                              <span className="text-xl sm:text-2xl font-bold text-purple-600">
                                ₪{parseFloat(bundle.bundle_price).toFixed(2)}
                              </span>
                            </div>

                            {/* Products List */}
                            {products.length > 0 && (
                              <div className="mb-3 bg-purple-50 rounded-lg p-2">
                                <p className="text-[10px] sm:text-xs font-semibold text-purple-900 mb-1">
                                  מוצרים בחבילה:
                                </p>
                                <ul className="space-y-0.5 text-[10px] sm:text-xs text-purple-700">
                                  {products.slice(0, 3).map((product, idx) => (
                                    <li key={idx} className="flex items-center gap-1">
                                      <span className="text-purple-400">•</span>
                                      <span className="line-clamp-1">{product?.name}</span>
                                    </li>
                                  ))}
                                  {products.length > 3 && (
                                    <li className="text-purple-500 font-medium">
                                      + עוד {products.length - 3} מוצרים
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}

                            {/* Add Button */}
                            <button
                              type="button"
                              onClick={() => addBundle(bundle)}
                              className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-2.5 sm:py-3 rounded-lg font-bold hover:from-purple-700 hover:to-purple-800 active:from-purple-800 active:to-purple-900 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 touch-manipulation min-h-[48px] text-sm sm:text-base"
                            >
                              <GiftIcon className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" aria-hidden="true" />
                              <span>הוסף חבילה</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Cart Sidebar - Hidden on tablets, shown on desktop */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-20 flex flex-col max-h-[calc(100vh-6rem)]">
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden flex flex-col h-full">
                {/* Header */}
                <div className="bg-gradient-to-r from-primary-600 to-blue-600 p-3 flex-shrink-0">
                  <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-2">
                      <ShoppingCartIcon className="h-5 w-5" aria-hidden="true" />
                      <h2 className="text-base font-bold">סיכום הזמנה</h2>
                    </div>
                    {selectedProducts.length > 0 && (
                      <div className="bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full text-sm font-bold">
                        {selectedProducts.length}
                      </div>
                    )}
                  </div>
                </div>

                {/* Cart Items - Scrollable */}
                <div className="flex-1 overflow-y-auto p-3">
                  {selectedProducts.length === 0 ? (
                    <div className="text-center py-8">
                      <ShoppingCartIcon className="mx-auto h-12 w-12 text-gray-300 mb-3" aria-hidden="true" />
                      <p className="text-sm text-gray-500 font-medium">העגלה ריקה</p>
                      <p className="text-xs text-gray-400 mt-1">התחל להוסיף מוצרים</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Bundle Groups */}
                      {Object.entries(groupedItems.bundles).map(([bundleRef, bundleGroup]) => (
                        <div key={bundleRef} className="bg-purple-50 border-2 border-purple-300 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <GiftIcon className="h-5 w-5 text-purple-600" aria-hidden="true" />
                              <span className="font-bold text-purple-900 text-sm">
                                {bundleGroup.title}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeBundle(bundleRef)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                              aria-label="הסר חבילה"
                              title="הסר חבילה שלמה"
                            >
                              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </div>

                          {/* Bundle Items */}
                          <div className="space-y-1">
                            {bundleGroup.items.map((item, idx) => (
                              <div key={`${bundleRef}-${item.id}-${idx}`} className="flex items-center gap-2 bg-white rounded p-2">
                                <div className="w-10 h-10 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                                  {item.images?.[0] || item.image ? (
                                    <img
                                      src={item.images?.[0] || item.image}
                                      alt={item.name}
                                      className="w-full h-full object-cover cursor-pointer hover:opacity-75 transition-opacity"
                                      onClick={() => {
                                        setLightboxImage(item.images?.[0] || item.image);
                                        setLightboxAlt(item.name);
                                      }}
                                      title="לחץ להגדלה"
                                    />
                                  ) : (
                                    <PhotoIcon className="w-full h-full p-2 text-gray-400" aria-hidden="true" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-gray-900 truncate">{item.name}</p>
                                  <p className="text-xs text-purple-600 font-semibold">
                                    ₪{parseFloat(item.unit_price).toFixed(2)}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => addSingleItemFromBundle(item)}
                                  className="p-1.5 text-primary-600 hover:bg-primary-50 rounded transition-colors"
                                  aria-label="הוסף עוד אחד"
                                  title="הוסף מוצר נוסף במחיר מלא"
                                >
                                  <PlusIcon className="h-4 w-4" aria-hidden="true" />
                                </button>
                              </div>
                            ))}
                          </div>

                          {/* Bundle Total */}
                          <div className="mt-2 pt-2 border-t border-purple-200">
                            <div className="flex justify-between text-sm font-bold text-purple-900">
                              <span>סה"כ חבילה:</span>
                              <span>
                                ₪{bundleGroup.items.reduce((sum, item) => 
                                  sum + (parseFloat(item.unit_price) * item.quantity), 0
                                ).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Regular Products */}
                      {groupedItems.regular.map(product => {
                        const itemPrice = calculateItemPrice(product);
                        const hasAdjustment = priceAdjustments[product.id];
                        
                        return (
                          <div key={product.id} className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                            {/* Product Header */}
                            <div className="flex gap-2 mb-2">
                              <div className="w-12 h-12 flex-shrink-0 bg-white rounded overflow-hidden">
                                {product.images?.[0] ? (
                                  <img
                                    src={product.images[0]}
                                    alt={product.name}
                                    className="w-full h-full object-cover cursor-pointer hover:opacity-75 transition-opacity"
                                    onClick={() => {
                                      setLightboxImage(product.images[0]);
                                      setLightboxAlt(product.name);
                                    }}
                                    title="לחץ להגדלה"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <PhotoIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-xs font-semibold text-gray-900 truncate">
                                  {product.name}
                                </h4>
                                <div className="flex items-center justify-between mt-1">
                                  <div className="flex items-center bg-white rounded border border-gray-300">
                                    <button
                                      type="button"
                                      onClick={() => updateQuantity(product.id, product.quantity - 1, product.is_tester || false, product.is_bonus || false)}
                                      className="p-1 hover:bg-gray-100"
                                      aria-label="הפחת כמות"
                                    >
                                      <MinusIcon className="h-3 w-3 text-gray-600" aria-hidden="true" />
                                    </button>
                                    <span className="px-2 text-xs font-medium">{product.quantity}</span>
                                    <button
                                      type="button"
                                      onClick={() => updateQuantity(product.id, product.quantity + 1, product.is_tester || false, product.is_bonus || false)}
                                      className="p-1 hover:bg-gray-100"
                                      aria-label="הוסף כמות"
                                    >
                                      <PlusIcon className="h-3 w-3 text-gray-600" aria-hidden="true" />
                                    </button>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeProduct(product.id, product.is_tester || false, product.is_bonus || false)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    aria-label="הסר מוצר"
                                  >
                                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Price & Discount */}
                            <div className="space-y-2 text-xs">
                              <div className="flex items-center justify-between">
                                <span className="text-gray-600">מחיר:</span>
                                <span className="font-bold text-gray-900">
                                  ₪{itemPrice.toFixed(2)} × {product.quantity} = ₪{(itemPrice * product.quantity).toFixed(2)}
                                </span>
                              </div>

                              {/* Discount Controls */}
                              {product.is_bundle_item ? (
                                <div className="text-xs text-purple-600 flex items-center gap-1 bg-purple-100 p-2 rounded">
                                  <GiftIcon className="h-3 w-3" aria-hidden="true" />
                                  <span>מחיר חבילה - לא ניתן להנחה נוספת</span>
                                </div>
                              ) : !hasAdjustment ? (
                                <details className="group">
                                  <summary className="flex items-center gap-1 text-primary-600 cursor-pointer hover:text-primary-700">
                                    <ReceiptPercentIcon className="h-3 w-3" aria-hidden="true" />
                                    <span>הוסף הנחה</span>
                                  </summary>
                                  <div className="mt-2 p-2 bg-white rounded border border-primary-200 space-y-2">
                                    <div className="flex gap-1">
                                      <input
                                        type="number"
                                        placeholder="%"
                                        className="w-16 px-2 py-1 text-xs border border-gray-300 rounded"
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            const value = e.target.value;
                                            if (value) {
                                              applyDiscount(product.id, 'percentage', value, e.target.nextSibling.value || 'הנחה');
                                            }
                                          }
                                        }}
                                      />
                                      <input
                                        type="text"
                                        placeholder="סיבה (חובה)"
                                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                                      />
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        const container = e.target.closest('details').querySelector('div');
                                        const valueInput = container.querySelector('input[type="number"]');
                                        const reasonInput = container.querySelector('input[type="text"]');
                                        if (valueInput.value && reasonInput.value) {
                                          applyDiscount(product.id, 'percentage', valueInput.value, reasonInput.value);
                                          e.target.closest('details').open = false;
                                        } else {
                                          toast.error('נא למלא אחוזי הנחה וסיבה');
                                        }
                                      }}
                                      className="w-full bg-primary-600 text-white py-1 rounded text-xs font-medium hover:bg-primary-700"
                                    >
                                      החל הנחה
                                    </button>
                                  </div>
                                </details>
                              ) : (
                                <div className="bg-amber-50 border border-amber-300 rounded p-2">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-amber-900 font-bold">הנחה: {hasAdjustment.value}%</span>
                                    <button
                                      onClick={() => removeDiscount(product.id)}
                                      className="text-red-600 hover:text-red-700"
                                      aria-label="הסר הנחה"
                                    >
                                      <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                                    </button>
                                  </div>
                                  <p className="text-[10px] text-amber-700">{hasAdjustment.reason}</p>
                                </div>
                              )}

                              {/* Notes */}
                              <details className="group">
                                <summary className="flex items-center gap-1 text-gray-600 cursor-pointer hover:text-gray-800">
                                  <ChatBubbleLeftRightIcon className="h-3 w-3" aria-hidden="true" />
                                  <span>הוסף הערה</span>
                                </summary>
                                <textarea
                                  value={productNotes[product.id] || ''}
                                  onChange={(e) => setProductNotes({ ...productNotes, [product.id]: e.target.value })}
                                  placeholder="הערה למוצר זה..."
                                  rows="2"
                                  className="mt-2 w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                />
                              </details>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Footer - Summary & Submit */}
                {selectedProducts.length > 0 && (
                  <div className="border-t-2 border-gray-200 p-3 bg-gray-50 flex-shrink-0">
                    {/* Order Notes */}
                    <div className="mb-3">
                      <label htmlFor="order-notes" className="block text-xs font-medium text-gray-700 mb-1">
                        הערות להזמנה:
                      </label>
                      <textarea
                        id="order-notes"
                        value={orderNotes}
                        onChange={(e) => setOrderNotes(e.target.value)}
                        placeholder="הערות כלליות להזמנה..."
                        rows="2"
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    {/* Summary */}
                    <div className="space-y-1 mb-3">
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>ביניים</span>
                        <span className="font-semibold">₪{subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>מע"מ (18%)</span>
                        <span className="font-semibold">₪{tax.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold text-gray-900 pt-1.5 border-t border-gray-300">
                        <span>סה"כ</span>
                        <span className="text-primary-600">₪{total.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="button"
                      onClick={handleSubmitOrder}
                      disabled={submitting}
                      className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-3 rounded-lg font-bold text-sm hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          <span>שולח...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
                          <span>אשר והשלם הזמנה</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tablet/Mobile Sticky Cart */}
      <AgentStickyCart
        selectedProducts={selectedProducts}
        subtotal={subtotal}
        tax={tax}
        total={total}
        onRemoveProduct={removeProduct}
        onUpdateQuantity={(productId, newQuantity, product) => {
          const isTester = product?.is_tester || false;
          const isBonus = product?.is_bonus || false;
          if (newQuantity <= 0) {
            removeProduct(productId, isTester, isBonus);
          } else {
            setSelectedProducts(prev => 
              prev.map(p => 
                p.id === productId && 
                p.is_tester === isTester && 
                p.is_bonus === isBonus
                  ? { ...p, quantity: newQuantity }
                  : p
              )
            );
          }
        }}
        onSubmitOrder={handleSubmitOrder}
        submitting={submitting}
        companyName={company?.name}
      />

      {/* Image Lightbox */}
      <ImageLightbox
        src={lightboxImage}
        alt={lightboxAlt}
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
      />
    </div>
  );
};

export default AgentCreateOrderPage;
